// src/prototype/data/priorityMatrix.ts
// ============================================================
// PRIORITY MATRIX (Oct 21, v3): Financial Value x Ease of Implementation for every ranked use
// case, with the quadrant from the single rule in stage300FeaturedContent (quadrantFromAxes),
// so the matrix, the Quick Win badge and every list agree.
//
// Fully scored use cases (authored dimensions) plot their financial and feasibility dimension
// scores and their composite (VRI). Use cases not yet fully scored plot from the same estimates
// the ranking uses (ROI-based value axis, effort/time-based ease axis) and are marked
// `scored: false` so the chart can show them as estimates.
// ============================================================
import {
  OPP_TO_UC, financialEaseForUseCase, compositeForUseCase, quadrantFromAxes, type Quadrant,
} from './adapters/stage300FeaturedContent';

export interface RankedForMatrix {
  id: string;
  rank: number;
  title: string;
  category: string;
  weightedScore: number;
  bv_axis: number;
  ie_axis: number;
}

export interface MatrixPoint {
  id: string;
  rank: number;
  title: string;
  category: string;
  /** VRI composite, 0-100. */
  vri: number;
  financial: number;
  ease: number;
  scored: boolean;
  quadrant: Quadrant;
}

const round1 = (v: number) => Math.round(v * 10) / 10;

export function buildMatrixPoints(ranked: RankedForMatrix[]): MatrixPoint[] {
  return ranked.map((o) => {
    const uc = OPP_TO_UC[o.id];
    const fe = uc != null ? financialEaseForUseCase(uc) : undefined;
    const composite = uc != null ? compositeForUseCase(uc) : undefined;
    const vri = composite ?? o.weightedScore;
    const financial = fe?.financial ?? o.bv_axis;
    const ease = fe?.ease ?? o.ie_axis;
    return {
      id: o.id, rank: o.rank, title: o.title, category: o.category,
      vri: round1(vri), financial: round1(financial), ease: round1(ease),
      scored: Boolean(fe),
      quadrant: quadrantFromAxes(financial, ease),
    };
  });
}

/** Prioritized list: VRI descending; ties by rank. */
export function prioritized(points: MatrixPoint[]): MatrixPoint[] {
  return [...points].sort((a, b) => b.vri - a.vri || a.rank - b.rank);
}
